import { ChatMessage } from "@/configs/@types/socket";
import { LiveChatDetail } from "@/services/supervisorApi";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

type SupervisorMessageState = {
  /** Messages keyed by chatId */
  messages: Record<string, ChatMessage[]>;
  addMessage: (chatId: string, message: ChatMessage) => void;
  setMessages: (chatId: string, messages: ChatMessage[]) => void;
  clearChat: (chatId: string) => void;
  clearAll: () => void;
  removedChatId: string | null;
  setRemovedChatId: (chatId: string | null) => void;

  unreadCounts: Record<string, number>;
  incrementUnreadCount: (chatId: string) => void;
  clearUnreadCount: (chatId: string) => void;

  activeChatId: string | null;
  setActiveChatId: (chatId: string | null) => void;

  newChat: LiveChatDetail | null;
  setNewChat: (chat: LiveChatDetail | null) => void;
};

const initialState = {
  messages: {} as Record<string, ChatMessage[]>,
  removedChatId: null as string | null,
  unreadCounts: {} as Record<string, number>,
  activeChatId: null as string | null,
  newChat: null as LiveChatDetail | null,
};

export const useSupervisorMessageStore = create<SupervisorMessageState>()(
  devtools(
    (set) => ({
      ...initialState,

      addMessage: (chatId, message) =>
        set(
          (state) => {
            const existing = state.messages[chatId] || [];
            // Skip exact duplicate by id
            if (existing.some((m) => m.id === message.id)) return state;
            return {
              messages: {
                ...state.messages,
                [chatId]: [...existing, message],
              },
            };
          },
          false,
          { type: "supervisor-msg/addMessage", chatId }
        ),

      setMessages: (chatId, messages) =>
        set(
          (state) => ({
            messages: {
              ...state.messages,
              [chatId]: messages,
            },
          }),
          false,
          { type: "supervisor-msg/setMessages", chatId }
        ),

      clearChat: (chatId) =>
        set(
          (state) => {
            const copy = { ...state.messages };
            delete copy[chatId];
            return { messages: copy };
          },
          false,
          { type: "supervisor-msg/clearChat", chatId }
        ),

      clearAll: () =>
        set(initialState, false, { type: "supervisor-msg/clearAll" }),

      setRemovedChatId: (chatId) =>
        set({ removedChatId: chatId }, false, { type: "supervisor-msg/setRemovedChatId" }),

      incrementUnreadCount: (chatId) =>
        set(
          (state) => ({
            unreadCounts: {
              ...state.unreadCounts,
              [chatId]: (state.unreadCounts[chatId] || 0) + 1,
            },
          }),
          false,
          { type: "supervisor-msg/incrementUnreadCount", chatId }
        ),

      clearUnreadCount: (chatId) =>
        set(
          (state) => {
            const copy = { ...state.unreadCounts };
            delete copy[chatId];
            return { unreadCounts: copy };
          },
          false,
          { type: "supervisor-msg/clearUnreadCount", chatId }
        ),

      setActiveChatId: (chatId) =>
        set({ activeChatId: chatId }, false, { type: "supervisor-msg/setActiveChatId" }),

      setNewChat: (chat) =>
        set({ newChat: chat }, false, { type: "supervisor-msg/setNewChat" }),
    }),
    { name: "supervisor-message-store" }
  )
);
